import Handlebars from 'handlebars';
import QRCode from 'qrcode';
import {
  formatiereDatum,
  formatiereDatumZeit,
  formatiereFlaeche,
  formatiereJaNein,
  formatiereUhrzeit,
  formatiereWaehrung,
  formatiereZahl,
  formatiereZeitstempel,
} from './format.js';
// Nur Typen: zur Laufzeit entsteht dadurch keine Abhängigkeit und damit auch
// kein Modulzyklus.
import type { LayoutAbschnitt, LayoutMangel } from './layout-kontext.js';

/**
 * Handlebars-Helper für Layouts.
 *
 * Diese Datei ist der Kern der Rendering-Parität (CLAUDE.md §3.5): Browser-
 * Vorschau und PDF-Lambda registrieren exakt dieselben Helper auf einer
 * eigenen Handlebars-Instanz. Nichts hier darf `Date.now()`, Zufall oder
 * Umgebungszustand verwenden — sonst ist das PDF nicht mehr reproduzierbar.
 */

/** Karte von Foto-ID auf eine einbettbare URL (Data-URI oder Blob-URL). */
export type BildAufloeser = Record<string, string>;

const escape = Handlebars.Utils.escapeExpression;

const alsText = (wert: unknown): string => {
  if (wert === null || wert === undefined) return '';
  if (typeof wert === 'string') return wert;
  if (typeof wert === 'number' || typeof wert === 'boolean') return String(wert);
  return '';
};

const vergleiche = (links: unknown, operator: string, rechts: unknown): boolean => {
  switch (operator) {
    case 'gleich':
    case '==':
      return String(links ?? '') === String(rechts ?? '');
    case 'ungleich':
    case '!=':
      return String(links ?? '') !== String(rechts ?? '');
    case 'groesser':
    case '>':
      return Number(links) > Number(rechts);
    case 'kleiner':
    case '<':
      return Number(links) < Number(rechts);
    case 'enthaelt':
      if (Array.isArray(links)) return links.map(String).includes(String(rechts));
      return String(links ?? '').includes(String(rechts ?? ''));
    case 'leer':
      if (Array.isArray(links)) return links.length === 0;
      return links === null || links === undefined || String(links).trim() === '';
    case 'gesetzt':
      if (Array.isArray(links)) return links.length > 0;
      return links !== null && links !== undefined && String(links).trim() !== '';
    default:
      return Boolean(links);
  }
};

/** Erzeugt ein QR-SVG synchron — Handlebars-Helper dürfen nicht asynchron sein. */
export const qrCodeSvg = (text: string, groessePx = 96): string => {
  if (!text) return '';
  const qr = QRCode.create(text, { errorCorrectionLevel: 'M' });
  const groesse = qr.modules.size;
  const daten = qr.modules.data;
  const pfade: string[] = [];
  for (let y = 0; y < groesse; y += 1) {
    for (let x = 0; x < groesse; x += 1) {
      if (daten[y * groesse + x]) pfade.push(`M${x} ${y}h1v1h-1z`);
    }
  }
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${groesse} ${groesse}"`,
    ` width="${groessePx}" height="${groessePx}" shape-rendering="crispEdges" role="img" aria-label="QR-Code">`,
    `<rect width="${groesse}" height="${groesse}" fill="#ffffff"/>`,
    `<path d="${pfade.join('')}" fill="#000000"/>`,
    '</svg>',
  ].join('');
};

/**
 * Registriert alle Helper auf einer Handlebars-Instanz.
 * Rückgabe ist dieselbe Instanz, damit sich Aufrufe verketten lassen.
 */
export const registriereHelper = (hb: typeof Handlebars): typeof Handlebars => {
  hb.registerHelper('datum', (wert: unknown) => formatiereDatum(wert as string));
  hb.registerHelper('datumZeit', (wert: unknown) => formatiereDatumZeit(wert as string));
  hb.registerHelper('uhrzeit', (wert: unknown) => formatiereUhrzeit(wert as string));
  hb.registerHelper('zeitstempel', (wert: unknown) => formatiereZeitstempel(wert as string));

  hb.registerHelper('zahl', (wert: unknown, optionen?: Handlebars.HelperOptions) => {
    const stellen = Number(optionen?.hash?.stellen ?? 2);
    return formatiereZahl(wert as number, Number.isFinite(stellen) ? stellen : 2);
  });

  hb.registerHelper('waehrung', (wert: unknown, optionen?: Handlebars.HelperOptions) =>
    formatiereWaehrung(wert as number, String(optionen?.hash?.waehrung ?? 'EUR')),
  );

  hb.registerHelper('flaeche', (wert: unknown) => formatiereFlaeche(wert as number));
  hb.registerHelper('jaNein', (wert: unknown) => formatiereJaNein(wert));

  /** `{{#wenn a 'gleich' b}} … {{else}} … {{/wenn}}` */
  hb.registerHelper(
    'wenn',
    function wenn(
      this: unknown,
      links: unknown,
      operator: unknown,
      rechts: unknown,
      optionen?: Handlebars.HelperOptions,
    ) {
      // Zwei Aufrufformen: {{#wenn wert}} und {{#wenn a 'op' b}}
      const opts = (optionen ?? (rechts as Handlebars.HelperOptions)) as Handlebars.HelperOptions;
      const treffer =
        optionen === undefined
          ? vergleiche(links, String(operator ?? 'gesetzt'), undefined)
          : vergleiche(links, String(operator), rechts);
      return treffer ? opts.fn(this) : opts.inverse(this);
    },
  );

  /** `{{#jede liste}}{{@nummer}} {{name}}{{/jede}}` — 1-basierte Nummer. */
  hb.registerHelper(
    'jede',
    function jede(this: unknown, liste: unknown, optionen: Handlebars.HelperOptions) {
      const eintraege = Array.isArray(liste) ? liste : [];
      if (eintraege.length === 0) return optionen.inverse(this);
      return eintraege
        .map((eintrag, index) =>
          optionen.fn(eintrag, {
            data: hb.createFrame({
              ...(optionen.data ?? {}),
              index,
              nummer: index + 1,
              erster: index === 0,
              letzter: index === eintraege.length - 1,
              anzahl: eintraege.length,
            }),
          }),
        )
        .join('');
    },
  );

  /** `{{anzahl liste}}` */
  hb.registerHelper('anzahl', (liste: unknown) => (Array.isArray(liste) ? liste.length : 0));

  /**
   * `{{bildUrl foto}}` — löst eine Fotoreferenz über `@root.bilder` auf.
   * Fehlt das Bild, kommt ein leerer String zurück; das Layout entscheidet,
   * ob dann ein Platzhalter erscheint.
   */
  hb.registerHelper(
    'bildUrl',
    function bildUrl(this: unknown, foto: unknown, optionen?: Handlebars.HelperOptions) {
      const wurzel = (optionen?.data?.root ?? {}) as { bilder?: BildAufloeser };
      const bilder = wurzel.bilder ?? {};
      const id = typeof foto === 'string' ? foto : ((foto as { id?: string } | null)?.id ?? '');
      return new hb.SafeString(escape(bilder[id] ?? ''));
    },
  );

  /** `{{bild foto breite=480}}` — fertiges `<img>`-Tag oder leer. */
  hb.registerHelper(
    'bild',
    function bild(this: unknown, foto: unknown, optionen?: Handlebars.HelperOptions) {
      const wurzel = (optionen?.data?.root ?? {}) as { bilder?: BildAufloeser };
      const bilder = wurzel.bilder ?? {};
      const referenz = (typeof foto === 'string' ? { id: foto } : foto) as {
        id?: string;
        beschriftung?: string;
      } | null;
      const id = referenz?.id ?? '';
      const url = bilder[id];
      if (!url) return '';
      const breite = optionen?.hash?.breite;
      const stil = breite ? ` style="width:${Number(breite)}px"` : '';
      const alt = escape(referenz?.beschriftung ?? 'Foto');
      return new hb.SafeString(`<img class="uep-foto" src="${escape(url)}" alt="${alt}"${stil} />`);
    },
  );

  /** `{{signatur unterschrift}}` — SVG aus dem gespeicherten Pfad. */
  hb.registerHelper('signatur', (wert: unknown, optionen?: Handlebars.HelperOptions) => {
    const signatur = wert as
      { pfadSvg?: string; breite?: number; hoehe?: number } | null | undefined;
    if (!signatur?.pfadSvg) return '';
    const breite = Number(signatur.breite ?? 600);
    const hoehe = Number(signatur.hoehe ?? 200);
    const anzeigeBreite = Number(optionen?.hash?.breite ?? 220);
    const anzeigeHoehe = Math.round((anzeigeBreite / breite) * hoehe);
    return new hb.SafeString(
      `<svg class="uep-signatur" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${breite} ${hoehe}" ` +
        `width="${anzeigeBreite}" height="${anzeigeHoehe}" role="img" aria-label="Unterschrift">` +
        `<path d="${escape(signatur.pfadSvg)}" fill="none" stroke="#111827" stroke-width="3" ` +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>',
    );
  });

  /** `{{qrCode meta.verifikationsUrl groesse=96}}` */
  hb.registerHelper('qrCode', (wert: unknown, optionen?: Handlebars.HelperOptions) => {
    const text = alsText(wert);
    if (!text) return '';
    const groesse = Number(optionen?.hash?.groesse ?? 96);
    return new hb.SafeString(qrCodeSvg(text, Number.isFinite(groesse) ? groesse : 96));
  });

  /** `{{seitenumbruch}}` */
  hb.registerHelper(
    'seitenumbruch',
    () => new hb.SafeString('<div class="uep-seitenumbruch"></div>'),
  );

  /** `{{verbinde liste ", "}}` */
  hb.registerHelper('verbinde', (liste: unknown, trenner: unknown) => {
    const eintraege = Array.isArray(liste) ? liste : [];
    const zeichen = typeof trenner === 'string' ? trenner : ', ';
    return eintraege
      .filter((eintrag) => alsText(eintrag) !== '')
      .map(alsText)
      .join(zeichen);
  });

  /** `{{oder wert "—"}}` — Ersatzwert für leere Felder. */
  hb.registerHelper('oder', (...argumente: unknown[]) => {
    const werte = argumente.slice(0, -1);
    for (const wert of werte) {
      if (wert !== null && wert !== undefined && String(wert).trim() !== '') return wert as string;
    }
    return '';
  });

  /** `{{kurz text 60}}` */
  hb.registerHelper('kurz', (wert: unknown, laenge: unknown) => {
    const text = alsText(wert);
    const max = Number(laenge ?? 80);
    return text.length <= max ? text : `${text.slice(0, Math.max(0, max - 1))}…`;
  });

  /** `{{zeilen text}}` — Zeilenumbrüche zu `<br>`, Inhalt bleibt escaped. */
  hb.registerHelper('zeilen', (wert: unknown) => {
    const text = alsText(wert);
    if (!text) return '';
    return new hb.SafeString(escape(text).replace(/\r?\n/g, '<br />'));
  });

  return hb;
};

/**
 * Frische Handlebars-Instanz mit allen Helpern. Bewusst isoliert, damit im
 * Browser registrierte Layout-Partials nicht in die Lambda-Instanz lecken.
 */
export const erzeugeHandlebars = (): typeof Handlebars => registriereHelper(Handlebars.create());

/** Kontextform, die jedes Layout erhält (SPEC Kap. 5.3). */
export interface LayoutKontext {
  data: Record<string, unknown>;
  objekt: Record<string, unknown> | null;
  /** Übergebene Einheit. Fehlt bei Protokollen ohne Einheitenbezug. */
  einheit?: Record<string, unknown> | null;
  branding: Record<string, unknown>;
  /**
   * Aufbereitete Abschnitte — der eigentliche Protokollinhalt. Ohne sie rendert
   * das Standard-Layout nur Kopf, Fuß und Unterschriften.
   */
  abschnitte?: LayoutAbschnitt[];
  meta: {
    protokollNr: string;
    art: string;
    status: string;
    uebergabeDatum: string | null;
    templateVersion: number;
    dataHash: string | null;
    hashPraefix: string;
    erstelltAm: string;
    verifikationsUrl?: string;
    beteiligte: Array<Record<string, unknown>>;
    signaturen: Array<Record<string, unknown>>;
    /** Nummerierter Fotoanhang, Reihenfolge = Reihenfolge im Protokoll. */
    fotoAnhang: Array<{ nummer: number; id: string; beschriftung?: string }>;
    /** Flache Mängelübersicht über alle Abschnitte. */
    maengel?: LayoutMangel[];
  };
  bilder: BildAufloeser;
}

/** Kompiliert ein Layout und rendert es mit dem Kontext. */
export const rendereLayout = (
  layoutHtml: string,
  kontext: LayoutKontext,
  hb: typeof Handlebars = erzeugeHandlebars(),
): string => {
  const vorlage = hb.compile(layoutHtml, { noEscape: false, strict: false });
  return vorlage(kontext);
};
